
import React, { useState } from 'react';
import { StoryConfig, GENRES, TONES, AUDIENCES, ILLUSTRATION_STYLES, ParentControls, SYSTEM_VOICES, VoiceOption } from '../types';
import { Loader2, Wand2, Palette, Sparkles } from './Icons';

interface CreateStoryProps {
  onSubmit: (config: StoryConfig, voice: VoiceOption) => void;
  isLoading: boolean;
  loadingMessage?: string;
  childName?: string;
  parentControls?: ParentControls | null;
}

const CreateStory: React.FC<CreateStoryProps> = ({ onSubmit, isLoading, loadingMessage = "Weaving Story...", childName, parentControls }) => {
  const [config, setConfig] = useState<StoryConfig>({
    prompt: '',
    genre: GENRES[0],
    tone: TONES[0],
    illustrationStyle: ILLUSTRATION_STYLES[0],
    length: 'Medium',
    targetAudience: AUDIENCES[1]
  });
  const [selectedVoice, setSelectedVoice] = useState<VoiceOption>(SYSTEM_VOICES[0]);
  const [voiceError, setVoiceError] = useState('');

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!config.prompt.trim()) return;
    if (!selectedVoice) {
      setVoiceError('Please select a narration voice.');
      return;
    }
    setVoiceError('');
    onSubmit(config, selectedVoice);
  };

  return (
    <div className="w-full max-w-4xl mx-auto px-4 py-8 md:py-10 animate-slide-up pb-24">
      <div className="text-center mb-8 md:mb-10">
        <h2 className="text-3xl md:text-5xl font-serif font-bold mb-4">Craft a Story for {childName || 'Your Child'}</h2>
        <p className="text-gray-400 text-base md:text-lg">Tell us a bit about what you want, and let the forge do the rest.</p>
        {childName && (
            <div className="mt-4 inline-flex items-center gap-2 px-4 py-1 rounded-full bg-gold-500/10 border border-gold-500/30 text-gold-300 text-xs font-bold uppercase tracking-wider">
                <Sparkles className="w-3 h-3" />
                <span>Starring {childName}</span>
            </div>
        )}
      </div>

      <form onSubmit={handleSubmit} className="glass-panel p-6 md:p-10 rounded-2xl md:rounded-3xl shadow-2xl space-y-8 border border-white/5 relative overflow-hidden">
        {/* Decorative Background Glow */}
        <div className="absolute top-0 right-0 w-96 h-96 bg-forge-500/10 rounded-full blur-[100px] -z-10 pointer-events-none"></div>

        {/* Main Prompt */}
        <div className="space-y-3">
          <label className="block text-sm font-bold text-gold-500 uppercase tracking-wider">
            Story Idea
          </label>
          <textarea
            value={config.prompt}
            onChange={(e) => setConfig({ ...config, prompt: e.target.value })}
            placeholder={`e.g., ${childName || 'The main character'} discovers a hidden door in their bedroom...`}
            className="w-full h-32 bg-black/40 border border-white/10 rounded-2xl p-6 text-base md:text-xl text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-gold-500/50 focus:border-gold-500 transition-all resize-none shadow-inner"
            required
          />
        </div>

        {/* Controls Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-6">
          
          <div className="space-y-2">
            <label className="block text-xs font-bold text-gray-400 uppercase tracking-wider ml-1">Genre</label>
            <div className="relative">
              <select
                value={config.genre}
                onChange={(e) => setConfig({ ...config, genre: e.target.value })}
                className="w-full bg-black/40 border border-white/10 rounded-xl p-4 text-base text-white appearance-none focus:outline-none focus:border-gold-500 transition-colors cursor-pointer hover:bg-white/5"
              >
                {GENRES.filter(g => !parentControls?.excludedThemes.includes(g)).map(g => <option key={g} value={g} className="bg-gray-900">{g}</option>)}
              </select>
              <div className="absolute right-4 top-1/2 -translate-y-1/2 pointer-events-none text-gold-500">▼</div>
            </div>
          </div>

          <div className="space-y-2">
             <label className="flex items-center gap-2 text-xs font-bold text-gray-400 uppercase tracking-wider ml-1">
                 <Palette className="w-3 h-3" />
                 Illustration Style
             </label>
            <div className="relative">
              <select
                value={config.illustrationStyle}
                onChange={(e) => setConfig({ ...config, illustrationStyle: e.target.value })}
                className="w-full bg-black/40 border border-white/10 rounded-xl p-4 text-base text-white appearance-none focus:outline-none focus:border-gold-500 transition-colors cursor-pointer hover:bg-white/5"
              >
                {ILLUSTRATION_STYLES.map(s => <option key={s} value={s} className="bg-gray-900">{s}</option>)}
              </select>
               <div className="absolute right-4 top-1/2 -translate-y-1/2 pointer-events-none text-gold-500">▼</div>
            </div>
          </div>

          <div className="space-y-2">
            <label className="block text-xs font-bold text-gray-400 uppercase tracking-wider ml-1">Tone</label>
            <div className="relative">
              <select
                value={config.tone}
                onChange={(e) => setConfig({ ...config, tone: e.target.value })}
                className="w-full bg-black/40 border border-white/10 rounded-xl p-4 text-base text-white appearance-none focus:outline-none focus:border-gold-500 transition-colors cursor-pointer hover:bg-white/5"
              >
                {TONES.map(t => <option key={t} value={t} className="bg-gray-900">{t}</option>)}
              </select>
               <div className="absolute right-4 top-1/2 -translate-y-1/2 pointer-events-none text-gold-500">▼</div>
            </div>
          </div>

           <div className="space-y-2">
            <label className="block text-xs font-bold text-gray-400 uppercase tracking-wider ml-1">Audience Reading Level</label>
            <div className="relative">
              <select
                value={config.targetAudience}
                onChange={(e) => setConfig({ ...config, targetAudience: e.target.value })}
                className="w-full bg-black/40 border border-white/10 rounded-xl p-4 text-base text-white appearance-none focus:outline-none focus:border-gold-500 transition-colors cursor-pointer hover:bg-white/5"
              >
                {AUDIENCES.map(a => <option key={a} value={a} className="bg-gray-900">{a}</option>)}
              </select>
               <div className="absolute right-4 top-1/2 -translate-y-1/2 pointer-events-none text-gold-500">▼</div>
            </div>
          </div>

           <div className="space-y-2 md:col-span-2">
            <label className="block text-xs font-bold text-gray-400 uppercase tracking-wider ml-1">Length</label>
            <div className="flex bg-black/40 rounded-xl p-1 border border-white/10">
              {(['Short', 'Medium', 'Long'] as const).map((len) => {
                const ORDER = { Short: 0, Medium: 1, Long: 2 };
                const maxOrder = ORDER[parentControls?.maxStoryLength ?? 'Long'];
                const disabled = ORDER[len] > maxOrder;
                return (
                  <button
                    key={len}
                    type="button"
                    disabled={disabled}
                    onClick={() => !disabled && setConfig({ ...config, length: len })}
                    title={disabled ? `Max length set to ${parentControls?.maxStoryLength} by parent controls` : undefined}
                    className={`flex-1 py-3 text-sm font-bold rounded-lg transition-all ${
                      disabled ? 'text-gray-600 cursor-not-allowed' :
                      config.length === len ? 'bg-white/10 text-gold-300 shadow-lg border border-gold-500/30' : 'text-gray-500 hover:text-white hover:bg-white/5'
                    }`}
                  >
                    {len}{disabled ? ' 🔒' : ''}
                  </button>
                );
              })}
            </div>
          </div>
        </div>

        {/* Voice Picker */}
        <div className="space-y-3">
          <label className="block text-xs font-bold text-gray-400 uppercase tracking-wider ml-1">
            Narration Voice <span className="text-red-400">*</span>
          </label>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-2">
            {SYSTEM_VOICES.map(voice => (
              <button
                key={voice.id}
                type="button"
                onClick={() => { setSelectedVoice(voice); setVoiceError(''); }}
                className={`p-3 rounded-xl border text-sm font-medium text-left transition-all ${
                  selectedVoice?.id === voice.id
                    ? 'border-gold-500 bg-gold-500/10 text-gold-300'
                    : 'border-white/10 bg-white/5 text-gray-300 hover:bg-white/10'
                }`}
              >
                <div className="text-base mb-1">🎙️</div>
                <div className="font-semibold">{voice.displayName}</div>
                <div className="text-xs text-gray-500 mt-0.5">{voice.description}</div>
              </button>
            ))}
          </div>
          {voiceError && <p className="text-red-400 text-xs ml-1">{voiceError}</p>}
        </div>

        {/* Submit Action */}
        <div className="pt-6">
          <button
            type="submit"
            disabled={isLoading || !config.prompt.trim()}
            className="w-full py-5 btn-primary-gradient hover:brightness-110 rounded-2xl font-bold text-xl text-white shadow-xl shadow-forge-500/20 disabled:opacity-50 disabled:cursor-not-allowed transition-all flex items-center justify-center gap-3 transform active:scale-[0.99]"
          >
            {isLoading ? (
              <>
                <Loader2 className="animate-spin w-6 h-6" />
                <span>{loadingMessage}</span>
              </>
            ) : (
              <>
                <Wand2 className="w-6 h-6" />
                <span>Generate Story</span>
              </>
            )}
          </button>
        </div>
      </form>
    </div>
  );
};

export default CreateStory;
